import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Loader2, Sparkles, Users, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';

import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import PostCard from '@/components/social/PostCard';
import CreatePostModal from '@/components/social/CreatePostModal';
import StoryRail from '@/components/social/StoryRail';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * /feed — Instagram-meets-marketplace home for logged-in users.
 *
 * - Two tabs: Following · For You.
 * - Infinite scroll via cursor (`next_cursor`).
 * - "+" floating button opens CreatePostModal — only for vendors + Founding Non-Profits.
 * - Consumers can browse + engage but cannot post.
 */
export default function FeedPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('foryou');
  const [posts, setPosts] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [canPost, setCanPost] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const sentinel = useRef(null);

  const headers = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  }), []);

  const fetchFirst = useCallback(async (which) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/social/feed?tab=${which}&limit=12`, { headers: user ? headers() : {} });
      if (res.ok) {
        const d = await res.json();
        setPosts(d.posts || []);
        setCursor(d.next_cursor);
      }
    } finally { setLoading(false); }
  }, [headers, user]);

  const fetchMore = useCallback(async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await fetch(`${API}/social/feed?tab=${tab}&limit=12&before=${encodeURIComponent(cursor)}`, { headers: user ? headers() : {} });
      if (res.ok) {
        const d = await res.json();
        setPosts((prev) => [...prev, ...(d.posts || [])]);
        setCursor(d.next_cursor);
      }
    } finally { setLoadingMore(false); }
  }, [cursor, headers, loadingMore, tab, user]);

  useEffect(() => { fetchFirst(tab); }, [tab, fetchFirst]);

  useEffect(() => {
    if (!user) { setCanPost(false); return; }
    (async () => {
      try {
        const res = await fetch(`${API}/social/me/can-post`, { headers: headers() });
        if (res.ok) setCanPost((await res.json()).can_post);
      } catch {}
    })();
  }, [user, headers]);

  // Infinite scroll
  useEffect(() => {
    if (!sentinel.current) return undefined;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) fetchMore();
    }, { rootMargin: '500px' });
    obs.observe(sentinel.current);
    return () => obs.disconnect();
  }, [fetchMore]);

  const handleCreated = (post) => {
    setPosts((prev) => [post, ...prev]);
    toast.success('Post is live in your feed.');
  };
  const handleDeleted = (id) => setPosts((prev) => prev.filter((p) => p.post_id !== id));

  const TabBtn = ({ id, icon: Icon, children }) => (
    <button
      onClick={() => setTab(id)}
      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
        tab === id
          ? 'bg-yellow-400 text-black shadow-[0_4px_20px_-4px_rgba(250,204,21,0.5)]'
          : 'bg-white/5 text-zinc-300 hover:bg-white/10 border border-white/10'
      }`}
      data-testid={`feed-tab-${id}`}
    >
      <Icon size={14} /> {children}
    </button>
  );

  return (
    <div className="min-h-screen bg-[#0B0D2B] text-white">
      <Navbar />

      <main className="pt-24 pb-12" data-testid="feed-page">
        <div className="max-w-2xl mx-auto px-4 w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-syne">The Feed</h1>
            <p className="text-zinc-400 text-xs mt-0.5">Where Indigenous entrepreneurs share their work.</p>
          </div>
          <button onClick={() => fetchFirst(tab)} className="p-2 rounded-lg hover:bg-white/10 text-zinc-300" data-testid="feed-refresh" aria-label="Refresh">
            <RefreshCcw size={14} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-5">
          <TabBtn id="following" icon={Users}>Following</TabBtn>
          <TabBtn id="foryou" icon={Sparkles}>For You</TabBtn>
        </div>

        {/* Story rail (Instagram-style 24h) */}
        <StoryRail viewer={user} canPost={canPost} />

        {/* Mobile-only "Make Post" circular tile — replaces the floating FAB */}
        {canPost && (
          <div className="sm:hidden mb-5 flex items-center gap-3" data-testid="mobile-make-post">
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 hover:border-fuchsia-400/60 hover:bg-white/10 transition-all px-3 py-2.5 w-full"
              data-testid="feed-mobile-make-post"
              aria-label="Make a post"
            >
              <span className="relative w-14 h-14 rounded-full bg-gradient-to-br from-fuchsia-500 via-pink-500 to-amber-400 flex items-center justify-center shadow-[0_0_22px_rgba(236,72,153,0.35)] flex-shrink-0">
                <span className="absolute inset-[3px] rounded-full bg-[#0B0D2B] flex items-center justify-center">
                  <Plus size={22} strokeWidth={2.5} className="text-white" />
                </span>
              </span>
              <span className="min-w-0 flex-1 text-left">
                <span className="block text-white font-bold text-sm font-syne">Make a Post</span>
                <span className="block text-zinc-400 text-[11px]">Share an update, photo, or video</span>
              </span>
            </button>
          </div>
        )}

        {/* Sign-in nudge for anonymous viewers */}
        {!user && (
          <div className="rounded-2xl bg-fuchsia-500/10 border border-fuchsia-500/30 p-4 mb-5 text-sm text-zinc-200">
            <p className="font-semibold text-white mb-1">Sign in to like, comment, and follow.</p>
            <p className="text-zinc-300 text-xs mb-3">Browsing is free. Engagement requires an account.</p>
            <Link to="/auth" className="inline-block px-3 py-1.5 rounded-full bg-yellow-400 text-black text-xs font-bold">Sign in / Register</Link>
          </div>
        )}

        {/* Posts */}
        {loading && posts.length === 0 ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-fuchsia-400" /></div>
        ) : posts.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/10 p-10 text-center">
            <p className="text-white font-semibold">No posts yet</p>
            <p className="text-zinc-400 text-xs mt-1">
              {tab === 'following'
                ? "Follow some vendors and their posts will show here."
                : "When vendors share their work it'll appear here."}
            </p>
            {canPost && (
              <button onClick={() => setShowCreate(true)} className="mt-4 px-4 py-2 rounded-full bg-yellow-400 text-black text-xs font-bold" data-testid="feed-empty-create">
                Be the first — share a post
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-6" data-testid="feed-list">
            {posts.map((p) => (
              <PostCard key={p.post_id} post={p} viewer={user} onChange={() => fetchFirst(tab)} onDelete={handleDeleted} />
            ))}
            <div ref={sentinel} />
            {loadingMore && (
              <div className="flex items-center justify-center py-4"><Loader2 className="animate-spin text-fuchsia-400" size={16} /></div>
            )}
            {!cursor && posts.length > 0 && (
              <p className="text-center text-zinc-500 text-xs py-6">You're all caught up.</p>
            )}
          </div>
        )}
        </div>
      </main>

      {/* Floating "+" — desktop only; mobile uses the inline "Make a Post" tile */}
      {canPost && (
        <button
          onClick={() => setShowCreate(true)}
          className="hidden sm:flex fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-yellow-400 hover:bg-yellow-300 text-black shadow-[0_12px_32px_-8px_rgba(250,204,21,0.7)] items-center justify-center"
          data-testid="feed-create-fab"
          aria-label="Create post"
        >
          <Plus size={24} strokeWidth={3} />
        </button>
      )}
      {/* Hint for consumers */}
      {user && !canPost && (
        <div className="hidden sm:block fixed bottom-6 right-6 z-40 max-w-xs rounded-2xl bg-[#13153A]/95 border border-white/10 backdrop-blur-md p-3 text-xs text-zinc-300 shadow-xl">
          <p className="font-semibold text-white mb-0.5">Want to post?</p>
          <p>
            Posting is for vendors + Founding Non-Profits.{' '}
            <button onClick={() => navigate('/onboarding/vendor')} className="text-yellow-400 underline">Become a vendor →</button>
          </p>
        </div>
      )}

      <CreatePostModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={handleCreated} viewer={user} />

      <Footer />
    </div>
  );
}
